// 🚚 택배송장 관리 훅
import { useState, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../firebase';
import type { Application } from '../../../../types/application';
import type { TrackingManagementState, TrackingData, TrackingAction } from '../types/applicationStickyHeader.types';

export interface UseTrackingManagementReturn extends TrackingManagementState {
  setTrackingDialogOpen: (open: boolean) => void;
  setTrackingNumber: (number: string) => void;
  setTrackingType: (type: 'delivery' | 'return') => void;
  setTrackingSubmitting: (submitting: boolean) => void;
  setLastTrackingAction: (action: TrackingAction | null) => void;
  handleOpenTrackingDialog: (type: 'delivery' | 'return') => void;
  handleSaveTracking: () => Promise<void>;
}

/**
 * 택배송장 관리 기능을 제공하는 훅
 * - 택배송장 다이얼로그 관리
 * - 택배송장 저장 및 업데이트
 * - 발송/반품 송장 구분 관리
 */
export const useTrackingManagement = (
  application: Application,
  onTrackingUpdate?: (trackingData: TrackingData) => void,
  onSnackbarOpen?: () => void
): UseTrackingManagementReturn => {
  // 🚚 택배송장 관리 상태
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingType, setTrackingType] = useState<'delivery' | 'return'>('delivery');
  const [trackingSubmitting, setTrackingSubmitting] = useState(false);
  const [lastTrackingAction, setLastTrackingAction] = useState<TrackingAction | null>(null);

  // 🚚 택배송장 다이얼로그 열기
  const handleOpenTrackingDialog = useCallback((type: 'delivery' | 'return') => {
    setTrackingType(type);
    setTrackingNumber(
      type === 'delivery' 
        ? application.tracking_number || '' 
        : (application as any).return_tracking_number || ''
    );
    setTrackingDialogOpen(true);
  }, [application]);

  // 🚚 택배송장 저장
  const handleSaveTracking = useCallback(async () => {
    if (!application.id || !trackingNumber.trim()) return;
    setTrackingSubmitting(true);
    
    try {
      const ref = doc(db, 'applications', application.id);
      const updateData: any = {
        updated_at: serverTimestamp(),
      };
      
      if (trackingType === 'delivery') {
        updateData.tracking_number = trackingNumber.trim();
      } else {
        updateData.return_tracking_number = trackingNumber.trim();
      }
      
      await updateDoc(ref, updateData);
      
      // 🎯 부모 컴포넌트에 즉시 업데이트 알림 (화면 재갱신 없이 로컬 상태 업데이트)
      if (onTrackingUpdate) {
        onTrackingUpdate({
          type: trackingType,
          number: trackingNumber.trim()
        });
      }
      
      setLastTrackingAction({ type: trackingType, action: 'saved' });
      if (onSnackbarOpen) {
        onSnackbarOpen();
      }
      setTrackingDialogOpen(false);
      
      // 액션 상태 초기화 (3초 후)
      setTimeout(() => {
        setLastTrackingAction(null);
      }, 3000);
    } catch (error) {
      console.error('택배송장 저장 실패:', error);
      if (onSnackbarOpen) {
        onSnackbarOpen();
      }
      setLastTrackingAction({ type: trackingType, action: 'saved' }); // 에러 시에도 스낵바 표시
    } finally {
      setTrackingSubmitting(false);
    }
  }, [application.id, trackingNumber, trackingType, onTrackingUpdate, onSnackbarOpen]);

  return {
    trackingDialogOpen,
    trackingNumber,
    trackingType,
    trackingSubmitting,
    lastTrackingAction,
    setTrackingDialogOpen,
    setTrackingNumber,
    setTrackingType,
    setTrackingSubmitting,
    setLastTrackingAction,
    handleOpenTrackingDialog,
    handleSaveTracking,
  };
};